import { type FC, useEffect } from "react";
import { useAuthStore } from "@/store/auth.store";
import { Layout } from "@/components/layout";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useDashboardStore } from "@/store/dashboard.store";
import { container, formatTitle, item, roundPercentage } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { PlanName, ValidationSuscriptionButton } from "@/common/enums/enums";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { DonutChart } from "../../components/ui/DonutChart";
ChartJS.register(ArcElement, Tooltip, Legend);

export const Dashboard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUser, user, userData } = useAuthStore();
  const {
    loading: loadingDashboard,
    data: {
      charts: { monthlyPlanUsed },
    },
    fetch: fetchDashboard,
  } = useDashboardStore();

  const fetchAndSetAvailableMinutesRecording = useRecordingUsageStore(
    (state) => state.fetchAndSetAvailableMinutes
  );

  // Paid plans are treated as unlimited for display (skip fixed quota like 4000).
  const isPaidPlan =
    Boolean(userData?.planName) && userData?.planName !== PlanName.FREE_TRIAL;

  useEffect(() => {
    if (user?.email) {
      void getUser(user.email);
    }
  }, [user?.email, getUser]);

  useEffect(() => {
    if (!userData?.id) {
      return;
    }
    void fetchDashboard(userData.id);
    void fetchAndSetAvailableMinutesRecording(userData.id);
  }, [userData?.id, fetchDashboard, fetchAndSetAvailableMinutesRecording]);

  const minutesUsed = monthlyPlanUsed.minutesUsed;
  const totalMinutes = monthlyPlanUsed.totalMinutes;
  const chartData = isPaidPlan
    ? [0, 1]
    : [minutesUsed, Math.max(totalMinutes - minutesUsed, 0)];
  const percentage = isPaidPlan
    ? 0
    : roundPercentage(
        totalMinutes > 0 ? (100 * minutesUsed) / totalMinutes : 0
      );

  return (
    <Layout
      navbarTitle={t("dashboard.title")}
      breadcrumbs={[
        {
          name: t("dashboard.title"),
          href: "/analytics",
          current: true,
        },
      ]}
    >
      <div className="p-6 md:p-8 lg:p-10">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {/* Header */}
          <motion.div
            className="flex flex-col gap-2 sm:flex-row items-center justify-between"
            variants={item}
          >
            <h1 className="text-xl font-bold tracking-tight">
              {t("dashboard.hi")},{" "}
              {userData?.firstname && userData?.lastname
                ? `${formatTitle(userData.userPreference.title)}${userData.firstname} ${userData.lastname}`
                : user?.email}
            </h1>
            <SubscriptionOnlyButton
              validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
              onClick={async () => {
                await navigate({
                  to: "/visits",
                  search: { openNewVisit: true },
                });
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("dashboard.buttons.newVisit")}
            </SubscriptionOnlyButton>
          </motion.div>
          <motion.div
            className="flex flex-col gap-2 sm:flex-row items-center justify-between"
            variants={item}
          >
            <DonutChart
              centerText={t("dashboard.labels.monthlyPlanUsed")}
              colors={["#0399FF", "#EAEAF2"]}
              data={chartData}
              hoverColors={["#36aeff", "#d1d5db"]}
              loading={loadingDashboard}
              percentage={percentage}
              additionalInfo={[
                {
                  label: t("dashboard.labels.minutesUsed"),
                  value: minutesUsed,
                },
                {
                  label: t("dashboard.labels.totalMinutes"),
                  value: isPaidPlan ? "∞" : totalMinutes,
                },
              ]}
            />
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};
